import { ApolloProvider } from '@apollo/client';
import { SiteUrls } from '@tableau/shared-urls';
import { RestApiServiceParamsContext, RestApiServiceParams } from '@tableau/vizportalclient-common-components';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import React from 'react';
import { NavTelemetryEventEmitter } from './NavTelemetryEventEmitter';
import { ApiRequests } from './apiRequests/ApiRequests';
import { ApiRequestsContext } from './apiRequests/ApiRequestsContext';
import { DataCatalogApolloClient } from './apolloClientGenerator';
import { ErrorCatcher } from './error/components/ErrorCatcher';
import { FeatureFlags } from './featureFlags/FeatureFlags';
import { FeatureFlagsContext } from './featureFlags/FeatureFlagsContext';
import { ReactTelemetryContext, TelemetryContext } from './telemetry/TelemetryContext';
import { telemetrySender } from './telemetry/TelemetrySender';
import { UIActions } from './uiActions/UIActions';
import { UIActionsContext } from './uiActions/UIActionsContext';
import { SiteUrlsContext } from './urls/SiteUrlsContext';
import { UserAndSiteSettingsContext } from './utils/UserAndSiteSettingsContext';


export type SiteUrlsProviderProps = {
  readonly siteUrls: SiteUrls;
};

export type FeatureFlagsProviderProps = {
  readonly featureFlags: FeatureFlags;
};

export type ReactTelemetryContextProps = {
  readonly telemetryContext?: TelemetryContext;
}

export type DataCatalogProviderPropsBase = SiteUrlsProviderProps & FeatureFlagsProviderProps & ReactTelemetryContextProps & {
  readonly client: DataCatalogApolloClient;
} & RestApiServiceParams;

export type VizPortalDataCatalogProviderProps = {
  readonly apiRequests: ApiRequests;
  readonly uiActions: UIActions;
  readonly areUsersVisible: boolean; // This property is passed in from Vizportal, and is based off of User Visibility Mode and User Role
} & DataCatalogProviderPropsBase;

export type VizPortalDQWDataCatalogProviderProps = Omit<VizPortalDataCatalogProviderProps, 'areUsersVisible'>;

export type VizClientDataCatalogProviderProps = DataCatalogProviderPropsBase & {
  readonly children: JSX.Element;
  readonly telemetryVizClientApiCallback?: (eventType: string, payload: Record<string, unknown>) => void;
};

/**
 * Provider for the EmbeddableVizClientLineagePanel since it doesn't need the [[ApiRequests]] object
 */
export function VizClientDataCatalogProvider({ telemetryVizClientApiCallback, ...props }: VizClientDataCatalogProviderProps): JSX.Element {
  React.useEffect(() => {
    if (telemetryVizClientApiCallback) {
      telemetrySender.setTelemetryVizClientApiCallback(telemetryVizClientApiCallback);
    }

    return () => {
      if (telemetryVizClientApiCallback) {
        telemetrySender.resetTelemetryContractApiCallback();
      }
    }
  }, [telemetryVizClientApiCallback])

  return (
    <ErrorCatcher>
      <ApolloProvider client={props.client}>
        <RestApiServiceParamsProvider siteLuid={props.siteLuid}>
          <QueryClientProvider client={new QueryClient()}>
            <SiteUrlsContext.Provider value={props.siteUrls}>
              <ReactTelemetryContext.Provider value={props.telemetryContext ?? {}}>
                <FeatureFlagsContext.Provider value={props.featureFlags}>
                  {props.children}
                </FeatureFlagsContext.Provider>
              </ReactTelemetryContext.Provider>
            </SiteUrlsContext.Provider>
          </QueryClientProvider>
        </RestApiServiceParamsProvider>
      </ApolloProvider>
    </ErrorCatcher>
  )
}

export class FeatureFlagsProvider extends React.Component<FeatureFlagsProviderProps> {
  public override render(): JSX.Element {
    return (
      <FeatureFlagsContext.Provider value={this.props.featureFlags}>
        {this.props.children}
      </FeatureFlagsContext.Provider>
    );
  }
}

/**
 * Wrapper for exported components when they only need the shared URLs context.
 */
export class SiteUrlsProvider extends React.Component<SiteUrlsProviderProps> {
  public override render(): JSX.Element {
    return (
      <SiteUrlsContext.Provider value={this.props.siteUrls}>
        {this.props.children}
      </SiteUrlsContext.Provider>
    );
  }
}

export class RestApiServiceParamsProvider extends React.Component<RestApiServiceParams>{
  public override render(): JSX.Element {
    return (
      <RestApiServiceParamsContext.Provider value={{ siteLuid: this.props.siteLuid }}>
        {this.props.children}
      </RestApiServiceParamsContext.Provider>
    );
  }
}

/**
 * Provider for lineage browser components
 */
export class LineageBrowserDataCatalogProvider extends React.Component<VizPortalDataCatalogProviderProps> {

  public override render(): JSX.Element {
    return (
      <ErrorCatcher>
        <>
          <NavTelemetryEventEmitter uiActions={this.props.uiActions} />
          <ApolloProvider client={this.props.client}>
            <RestApiServiceParamsProvider siteLuid={this.props.siteLuid}>
              <QueryClientProvider client={new QueryClient()}>
                <SiteUrlsContext.Provider value={this.props.siteUrls}>
                  <FeatureFlagsContext.Provider value={this.props.featureFlags}>
                    <ApiRequestsContext.Provider value={this.props.apiRequests}>
                      <UIActionsContext.Provider value={this.props.uiActions}>
                        <ReactTelemetryContext.Provider value={this.props.telemetryContext ?? {}}>
                          <UserAndSiteSettingsContext.Provider value={{ areUsersVisible: this.props.areUsersVisible }}>
                            {this.props.children}
                          </UserAndSiteSettingsContext.Provider>
                        </ReactTelemetryContext.Provider>
                      </UIActionsContext.Provider>
                    </ApiRequestsContext.Provider>
                  </FeatureFlagsContext.Provider>
                </SiteUrlsContext.Provider>
              </QueryClientProvider>
            </RestApiServiceParamsProvider>
          </ApolloProvider>
        </>
      </ErrorCatcher>
    );
  }
}

/**
 * Provider for the data catalog stuff in VizPortal's search results page.
 *
 * Note (abrandon 2020-03-23) This probably contains more providers than we actually need for search results, but I don't want to risk
 * removing some and accidentally breaking something. If we ever get to a point where it's difficult to pass in the required info,
 * it might be possible to remove the provider in question.
 */
export class VizPortalSearchResultsDataCatalogProvider extends React.Component<VizPortalDataCatalogProviderProps> {

  public override render(): JSX.Element {
    return (
      <ErrorCatcher>
        <>
          <ApolloProvider client={this.props.client}>
            <RestApiServiceParamsProvider siteLuid={this.props.siteLuid}>
              <QueryClientProvider client={new QueryClient()}>
                <SiteUrlsContext.Provider value={this.props.siteUrls}>
                  <FeatureFlagsContext.Provider value={this.props.featureFlags}>
                    <ApiRequestsContext.Provider value={this.props.apiRequests}>
                      <UIActionsContext.Provider value={this.props.uiActions}>
                        <UserAndSiteSettingsContext.Provider value={{ areUsersVisible: this.props.areUsersVisible }}>
                          {this.props.children}
                        </UserAndSiteSettingsContext.Provider>
                      </UIActionsContext.Provider>
                    </ApiRequestsContext.Provider>
                  </FeatureFlagsContext.Provider>
                </SiteUrlsContext.Provider>
              </QueryClientProvider>
            </RestApiServiceParamsProvider>
          </ApolloProvider>
        </>
      </ErrorCatcher>
    );
  }
}

/**
 * Provider for DQW components in VizPortal.
 */
export class VizPortalDQWDataCatalogProvider extends React.Component<VizPortalDQWDataCatalogProviderProps> {

  public override render(): JSX.Element {
    return (
      <ErrorCatcher>
        <>
          <ApolloProvider client={this.props.client}>
            <RestApiServiceParamsProvider siteLuid={this.props.siteLuid}>
              <QueryClientProvider client={new QueryClient()}>
                <SiteUrlsContext.Provider value={this.props.siteUrls}>
                  <FeatureFlagsContext.Provider value={this.props.featureFlags}>
                    <ApiRequestsContext.Provider value={this.props.apiRequests}>
                      <UIActionsContext.Provider value={this.props.uiActions}>
                        {this.props.children}
                      </UIActionsContext.Provider>
                    </ApiRequestsContext.Provider>
                  </FeatureFlagsContext.Provider>
                </SiteUrlsContext.Provider>
              </QueryClientProvider>
            </RestApiServiceParamsProvider>
          </ApolloProvider>
        </>
      </ErrorCatcher>
    );
  }
}
